import { logError } from "./log";

/**
 * BeforeInstallPromptEvent — Chromium-only, not in lib.dom yet.
 *
 * Defining a minimal local interface keeps the install prompt code
 * strict-typed without pulling a third-party type package in. Spec:
 * https://web.dev/customize-install/
 *
 * The runtime feature-detect (`"BeforeInstallPromptEvent" in window`
 * or, more reliably, a captured `beforeinstallprompt` event) gates
 * any use — Safari and Firefox don't fire this event.
 */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Module-level state. `deferredPrompt` holds the captured Chromium
// `beforeinstallprompt` event between the moment Chromium fires it
// and the moment the user clicks "Install". `subscribers` lets the
// React tree re-render when availability flips (capture / install).
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

/**
 * Notify every subscriber, isolating each callback from the others.
 * A throwing subscriber would otherwise drop all later notifications
 * silently — the React-component subscriber today is a `setState`
 * call so it can't throw, but the pub/sub utility shouldn't depend
 * on that. Errors are swallowed (not logged) because subscribers
 * are responsible for their own error handling at the call site.
 */
function notifySubscribers(): void {
  for (const cb of subscribers) {
    try {
      cb();
    } catch (err) {
      // Swallow so one throwing subscriber can't block the others
      // — but emit a structured log line so the failure isn't
      // completely silent. Subscribers still own their own error
      // handling at the call site; this is a debug breadcrumb.
      logError("ui", "install_prompt_subscriber_failed", err);
    }
  }
}
// Idempotency guard: HMR + StrictMode dev-loop can re-execute this
// module / call site. Without the guard, every re-run would attach
// another `beforeinstallprompt` + `appinstalled` listener pair to
// `window`, multiplying subscriber notifications per event.
let captured = false;

/**
 * Capture the deferred install prompt the moment Chromium fires it
 * and stash it in a module-level slot. Browsers fire
 * `beforeinstallprompt` once early in the page lifecycle; calling
 * `event.preventDefault()` lets the app surface its own install UI
 * later instead of letting the browser show its mini-infobar.
 *
 * The captured event is consumed (and cleared) by `triggerInstall`
 * below — `prompt()` can only be called once per event instance per
 * spec.
 *
 * Call this once at app boot from `main.tsx` so the listener is
 * attached before the browser's first eligibility check. Idempotent
 * — additional calls are no-ops, so dev-time HMR / StrictMode
 * re-execution of `main.tsx` doesn't multiply listeners.
 */
export function captureInstallPrompt(): void {
  if (typeof window === "undefined") return;
  if (captured) return;
  captured = true;
  window.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notifySubscribers();
  });
  // Clear the slot when the user actually installs — same event
  // (`appinstalled`) fires regardless of whether the install came
  // from our UI or the browser's own menu, so this also keeps the
  // "Install" button hidden post-install.
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notifySubscribers();
  });
}

/**
 * Subscribe to install-availability changes. The callback fires when
 * `beforeinstallprompt` is captured (install becomes available) or
 * when `appinstalled` fires (install becomes unavailable). Returns
 * an unsubscribe function — wire from a `useEffect` cleanup.
 */
export function subscribeInstallAvailability(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * localStorage key set when the user dismisses the install banner
 * via the host UI's `dismissInstallPrompt` (the `✕` button). When
 * present, `isInstallAvailable` returns false even if a prompt has
 * been captured — so the banner stays hidden across page reloads.
 *
 * Chromium's own post-dismiss cooldown (~90 days after a "dismissed"
 * outcome at the native dialog) handles the case where the user
 * clicks Install → "Not now"; the localStorage flag covers the
 * complementary case where the user explicitly dismisses our banner
 * before getting that far.
 *
 * Why `localStorage` and not `idb-keyval` via `StorageAdapter`: the
 * consumer needs a synchronous read API (`isInstallAvailable` is
 * called from React render) and the value is a non-sensitive UI
 * preference. `apps/web/CLAUDE.md` documents this exception in the
 * storage table — anything carrying user data still routes through
 * `StorageAdapter`.
 */
export const INSTALL_DISMISSED_KEY = "reflecto.install_dismissed";

// Session-only fallback for `dismissInstallPrompt` when localStorage
// is unavailable or throws (private-mode Safari, quota-full, disabled
// storage). Without this flag, a failed `setItem` would silently
// flow through to `notifySubscribers` but `isInstallAvailable` would
// still return true (because `isPersistedDismissed` also catches the
// read error and returns false), leaving the banner visible despite
// the user explicitly dismissing it.
let sessionDismissed = false;

/**
 * Single source of truth for whether `localStorage` is reachable.
 * Both read + write paths route through this so the guard style
 * stays consistent (the previous file mixed `=== "undefined"` and
 * `!== "undefined"` shapes for the same check).
 *
 * Note this only checks the global presence — `getItem`/`setItem`
 * can still throw at call time in private-mode Safari / quota-full /
 * disabled-storage contexts. Each call site wraps its own try/catch.
 */
function hasLocalStorage(): boolean {
  return typeof localStorage !== "undefined";
}

function isPersistedDismissed(): boolean {
  if (!hasLocalStorage()) return false;
  try {
    return localStorage.getItem(INSTALL_DISMISSED_KEY) === "1";
  } catch {
    // localStorage can throw in private-mode Safari / quota-full /
    // disabled-storage contexts. Treat any failure as "not
    // persisted" — the session flag above still guards the in-tab
    // dismiss for these contexts.
    return false;
  }
}

/** Whether an install prompt is currently available to trigger. */
export function isInstallAvailable(): boolean {
  return deferredPrompt !== null && !sessionDismissed && !isPersistedDismissed();
}

/**
 * Persist a "user dismissed without installing" flag and notify
 * subscribers so the banner hides immediately. The captured prompt
 * itself isn't cleared — Chromium may re-fire `beforeinstallprompt`
 * later (typically after a multi-day cooldown) which would re-set
 * `deferredPrompt`, but `isInstallAvailable` will still return false
 * thanks to the persisted flag (or the session fallback).
 *
 * The session flag is set BEFORE `notifySubscribers()` so the
 * subscriber's re-read of `isInstallAvailable()` sees the dismissed
 * state even if `localStorage.setItem` threw — without it, the
 * banner would silently re-render as visible in private-mode Safari /
 * quota-full / disabled-storage contexts.
 *
 * Idempotent — safe to call multiple times. The second + later
 * calls short-circuit before re-writing the persisted flag and
 * before notifying subscribers, so a future analytics-style
 * subscriber won't double-fire on duplicate dismiss clicks.
 */
export function dismissInstallPrompt(): void {
  if (sessionDismissed) return;
  // Set session flag FIRST so subscribers see the dismissed state
  // even when persistence fails. Doing this last would let the
  // subscriber's `isInstallAvailable()` re-read return true.
  sessionDismissed = true;
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
    } catch {
      // Persistence failed (private-mode, quota-full, etc.) — the
      // sessionDismissed flag above keeps the banner hidden for
      // the rest of this tab. Next page load loses the dismissal.
    }
  }
  notifySubscribers();
}

/**
 * Trigger the captured install prompt and wait for the user choice.
 * Resolves to the outcome string. The captured event is consumed
 * either way — Chromium's spec forbids re-prompting the same event
 * instance, so a second call (after a "dismissed") will return
 * `"unavailable"` until the browser re-fires `beforeinstallprompt`
 * (typically after a multi-day cooldown the platform owns).
 *
 * Subscriber notification is deferred until AFTER `event.userChoice`
 * resolves so the install banner stays visible while the system
 * dialog is open — a UX gap would appear if we cleared the slot
 * (and notified subscribers) up-front. The captured event itself is
 * consumed in a try/finally so a `prompt()` rejection still releases
 * the slot for the next browser-fired prompt instead of stranding
 * the banner permanently.
 *
 * Re-entrancy: JS is single-threaded but `await` suspension points
 * still allow other tasks to run, so a second `triggerInstall()`
 * arriving between `await` boundaries could capture the same non-
 * null `deferredPrompt` and try to double-prompt the consumed event
 * — invalid per spec. In practice the OS install dialog blocks all
 * UI interaction the moment it opens, so a second click can't
 * register before this call finishes; the `inFlight` lock below is
 * defensive insurance for non-button trigger surfaces (programmatic
 * callers, future keyboard shortcut, etc.).
 */
let inFlight = false;
export async function triggerInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (inFlight) return "unavailable";
  const event = deferredPrompt;
  if (!event) return "unavailable";
  inFlight = true;
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome;
  } finally {
    deferredPrompt = null;
    inFlight = false;
    notifySubscribers();
  }
}
